import { Button, Checkbox, Divider, Empty, Input, Space, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

interface FilterValueOption {
  value: string;
  label: string;
}

interface ColumnFilterDropdownProps {
  column: string;
  fetchValues: (params: {
    search: string;
    take: number;
    otherFilters: Record<string, string[]>;
    searchText: string;
  }) => Promise<{ values: FilterValueOption[]; total: number }>;
  selectedValues: string[];
  otherFilters: Record<string, string[]>;
  searchText: string;
  onApply: (column: string, values: string[]) => void;
  onClear: (column: string) => void;
  confirm: () => void;
  close: () => void;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const ColumnFilterDropdown = ({
  column,
  fetchValues,
  selectedValues,
  otherFilters,
  searchText,
  onApply,
  onClear,
  confirm,
  close,
}: ColumnFilterDropdownProps) => {
  const [searchWithinList, setSearchWithinList] = useState("");
  const [candidates, setCandidates] = useState<FilterValueOption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<string[]>(selectedValues);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);

  useEffect(() => {
    setCheckedKeys(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(false);
      fetchValues({
        search: searchWithinList,
        take: PAGE_SIZE,
        otherFilters,
        searchText,
      })
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          setCandidates(result.values);
          setTotal(result.total);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setCandidates([]);
          setTotal(0);
          setError(true);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWithinList, JSON.stringify(otherFilters), searchText]);

  const allVisibleChecked = useMemo(
    () =>
      candidates.length > 0 &&
      candidates.every((candidate) => checkedKeys.includes(candidate.value)),
    [candidates, checkedKeys],
  );

  const toggleValue = (value: string, checked: boolean) => {
    setCheckedKeys((prev) =>
      checked ? [...prev, value] : prev.filter((key) => key !== value),
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    const visibleValues = candidates.map((candidate) => candidate.value);
    setCheckedKeys((prev) =>
      checked
        ? Array.from(new Set([...prev, ...visibleValues]))
        : prev.filter((key) => !visibleValues.includes(key)),
    );
  };

  const handleOk = () => {
    onApply(column, checkedKeys);
    confirm();
    close();
  };

  const handleReset = () => {
    setCheckedKeys([]);
    onClear(column);
    confirm();
    close();
  };

  return (
    <div style={{ padding: 8, width: 240 }}>
      <Input
        placeholder="Buscar..."
        value={searchWithinList}
        onChange={(e) => setSearchWithinList(e.target.value)}
        allowClear
        size="small"
      />
      <Divider style={{ margin: "8px 0" }} />
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {loading ? (
          <Space
            style={{ width: "100%", justifyContent: "center", padding: 12 }}
          >
            <Spin size="small" />
          </Space>
        ) : error ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No se pudieron cargar los valores. Reintenta escribiendo en el buscador."
          />
        ) : candidates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Sin valores"
          />
        ) : (
          <>
            <div style={{ marginBottom: 4 }}>
              <Checkbox
                checked={allVisibleChecked}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              >
                Seleccionar todo
              </Checkbox>
            </div>
            {candidates.map((candidate) => (
              <div key={candidate.value}>
                <Checkbox
                  checked={checkedKeys.includes(candidate.value)}
                  onChange={(e) =>
                    toggleValue(candidate.value, e.target.checked)
                  }
                >
                  {candidate.label}
                </Checkbox>
              </div>
            ))}
            {total > candidates.length && (
              <div style={{ color: "#999", fontSize: 12, marginTop: 4 }}>
                Mostrando {candidates.length} de {total} — refina la búsqueda
              </div>
            )}
          </>
        )}
      </div>
      <Divider style={{ margin: "8px 0" }} />
      <Space style={{ display: "flex", justifyContent: "space-between" }}>
        <Button size="small" onClick={handleReset}>
          Restablecer
        </Button>
        <Button size="small" type="primary" onClick={handleOk}>
          Aceptar
        </Button>
      </Space>
    </div>
  );
};

export default ColumnFilterDropdown;
